import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button, CircularProgress, Snackbar } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { api, errorKey, type ApiErrorBody } from '../api/client';
import { currentLocale } from '../i18n';

interface ExportButtonProps {
  /** Path under `/export`, e.g. `products.xlsx`. */
  file: string;
  /** The same filters the list is showing, so the sheet matches the screen. */
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Downloads a server-generated spreadsheet.
 *
 * It goes through the authenticated axios client rather than a plain link,
 * because the export endpoints require a bearer token and a link cannot carry
 * one. That means holding the file in memory as a blob and clicking a temporary
 * anchor — the standard trick, and fine for sheets of this size.
 */
export function ExportButton({ file, params }: ExportButtonProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failureKey, setFailureKey] = useState<string | null>(null);

  async function download(): Promise<void> {
    setBusy(true);
    setFailureKey(null);
    try {
      const response = await api.get<Blob>(`/export/${file}`, {
        responseType: 'blob',
        params: {
          // Headers and number formats follow the interface language.
          locale: currentLocale(),
          ...Object.fromEntries(
            Object.entries(params ?? {}).filter(
              ([, value]) => value !== undefined && value !== '',
            ),
          ),
        },
      });

      // The server sets the filename; fall back to the requested path.
      const disposition = String(response.headers['content-disposition'] ?? '');
      const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1];
      const plain = /filename="([^"]+)"/.exec(disposition)?.[1];
      const filename = encoded ? decodeURIComponent(encoded) : (plain ?? file);

      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      // Revoking immediately can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      // With responseType 'blob' the error body arrives as a Blob too, so the
      // usual `data.messageKey` lookup would miss it and report "unexpected".
      setFailureKey(await blobErrorKey(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={() => void download()}
        disabled={busy}
      >
        {t('common.export')}
      </Button>

      <Snackbar
        open={Boolean(failureKey)}
        autoHideDuration={5000}
        onClose={() => setFailureKey(null)}
        message={failureKey ? t(failureKey) : ''}
      />
    </>
  );
}

/** Reads the i18n key out of an error whose body is a Blob. */
async function blobErrorKey(error: unknown): Promise<string> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const body = JSON.parse(await error.response.data.text()) as ApiErrorBody;
      return body.messageKey ?? 'errors.unexpected';
    } catch {
      return 'errors.unexpected';
    }
  }
  return errorKey(error);
}
